import { inviteUserToTeam } from '@modules/api';
import { ALL_GRADE_OPTIONS, FEMALE, MALE, STATUS, TEAM_ROLE } from '@type/db-types';
import { formatAsISOJan1st } from './date-utils';
import { ACTIVE_SINCE_OPTIONS } from '@components/AddWrestler/AddWrestlerUtils';
import { UserImport } from '@components/teamCreation/ImportModal';
import { log } from './logging';

export const TEAM_MEMBER_COLUMNS = [
    {
        name: 'First Name',
        key: 'firstName',
        required: true,
        description: 'The first name of the user',
        suggested_mappings: ['First', 'First Name'],
    },
    {
        name: 'Last Name',
        key: 'lastName',
        required: true,
        description: 'The last name of the user',
        suggested_mappings: ['Last', 'Last Name'],
    },
    {
        name: 'Weight',
        key: 'weight',
        required: true,
        description: 'users weight',
        suggested_mappings: ['WT', 'WT.', 'Weight (lbs)', 'weight', 'lbs'],
    },
    {
        name: 'Birthday',
        key: 'birthday',
        required: true,
        description: 'users birthday',
        suggested_mappings: ['DOB', 'Birthday'],
    },
    {
        name: 'Gender',
        key: 'gender',
        required: false,
        description: 'users gender',
        suggested_mappings: ['sex'],
    },
    {
        name: 'Grade',
        key: 'grade',
        data_type: 'text',
        description: 'Users grade',
        suggested_mappings: ['grade'],
        required: false,
    },
    {
        name: 'Experience (years wrestling)',
        key: 'experience',
        data_type: 'text',
        description: 'years user has been wrestling',
        suggested_mappings: ['exp', 'experience'],
        required: false,
    },
    {
        name: 'Phone Number',
        key: 'phone',
        data_type: 'text',
        description: 'users telephone #',
        suggested_mappings: ['#', 'tel', 'phone number', 'number'],
        required: false,
    },
    {
        name: 'Year Started Wrestling',
        key: 'activeSince',
        data_type: 'text',
        description:
            'year user started wrestling, for 2023-2024 season, enter: 2023. NOTE: "experience" overrides this field',
        suggested_mappings: ['startedAt', 'year started', 'year'],
        required: false,
    },
];

export const handleTeamMemberImport = (
    { firstName, lastName, weight, birthday, grade, experience, activeSince, gender }: UserImport,
    teamId: string,
) =>
    inviteUserToTeam({
        firstName,
        lastName,
        gender: parseGender(gender),
        weight,
        birthday,
        grade: parseGrade(grade),
        email: '',
        role: TEAM_ROLE.ATHLETE,
        teamId,
        teamStatus: STATUS.ACTIVE,
        activeSince: getActiveSince(activeSince, experience),
        userId: '',
    });

const getActiveSince = (
    activeSince: string | undefined,
    experience: number | string | undefined,
): string => {
    const thisYear = new Date().getFullYear();
    const thisJanuary = formatAsISOJan1st(new Date());

    if (!activeSince && !experience) {
        return thisJanuary;
    } else if (experience) {
        return formatAsISOJan1st(`${(thisYear - Number(experience)).toString()}-01-02`); // use second day to avoid timezone issues
    } else {
        return (
            ACTIVE_SINCE_OPTIONS.find(({ value }) => value === activeSince)?.value || thisJanuary
        );
    }
};

const parseGrade = (grade: string | undefined): string => {
    if (!grade) {
        return '';
    }
    if (grade.toLowerCase().includes('freshman')) {
        return '9';
    }
    if (grade.toLowerCase().includes('sophomore')) {
        return '10';
    }
    if (grade.toLowerCase().includes('junior')) {
        return '11';
    }
    if (grade.toLowerCase().includes('senior')) {
        return '12';
    }
    // handle kindergarten
    if (grade.toLowerCase().includes('kindergarten') || grade.toLowerCase().includes('k')) {
        return 'K';
    }
    // parse integer grade and find in ALL_GRADE_OPTIONS
    const parsedGrade = parseInt(grade);
    const foundGrade = ALL_GRADE_OPTIONS.find(
        ({ value }) => value === parsedGrade.toString() || value === grade,
    );

    if (foundGrade) {
        return foundGrade.value;
    }

    if (isNaN(parsedGrade)) {
        log(`NON Parsed grade: ${grade}`);
        return '';
    }

    log(`Not found grade: ${grade}`);
    return '';
};

const parseGender = (gender: string | undefined): string => {
    // match any string like male/female/girl/boy etc..
    if (!gender) {
        return '';
    }

    const lowerCaseGender = gender.toLowerCase();

    const maleKeywords = [MALE, 'man', 'boy', 'm', 'men', 'b'];
    const femaleKeywords = [FEMALE, 'woman', 'girl', 'f', 'g'];

    if (
        maleKeywords.some(
            (keyword) => lowerCaseGender.includes(keyword) || keyword[0] === lowerCaseGender[0],
        )
    ) {
        return MALE;
    }

    if (
        femaleKeywords.some(
            (keyword) => lowerCaseGender.includes(keyword) || keyword[0] === lowerCaseGender[0],
        )
    ) {
        return FEMALE;
    }

    return '';
};
